package pl.nickers.match3

const val MAX_LIVES = 5
const val LIFE_REFILL_INTERVAL_MS = 30L * 60L * 1000L

fun interface GemDebiter {
    fun debit(amount: Int): Boolean
}

object NoOpGemDebiter : GemDebiter {
    override fun debit(amount: Int): Boolean = false
}

data class LivesInfo(
    val current: Int,
    val max: Int = MAX_LIVES,
    val millisToNextLife: Long = 0L,
)

class LivesService(
    private val storage: GameStorage,
    private val clock: () -> Long = { currentTimeMillis() },
) {
    fun currentLives(): Int {
        recomputeRefill()
        return storage.livesCount
    }

    fun consumeLife(): Boolean {
        val before = currentLives()
        if (before <= 0) return false
        storage.livesCount = before - 1
        if (before == MAX_LIVES) storage.livesLastRefillTimestamp = clock()
        return true
    }

    fun timeToNextLife(): Long {
        val lives = currentLives()
        if (lives >= MAX_LIVES) return 0L
        val elapsed = (clock() - storage.livesLastRefillTimestamp).coerceAtLeast(0L)
        return (LIFE_REFILL_INTERVAL_MS - elapsed).coerceIn(0L, LIFE_REFILL_INTERVAL_MS)
    }

    fun refillFull() {
        storage.livesCount = MAX_LIVES
        storage.livesLastRefillTimestamp = clock()
    }

    fun info(): LivesInfo = LivesInfo(
        current = currentLives(),
        millisToNextLife = timeToNextLife(),
    )

    private fun recomputeRefill() {
        val now = clock()
        var lives = storage.livesCount.coerceIn(0, MAX_LIVES)
        var lastRefill = storage.livesLastRefillTimestamp
        if (lastRefill == 0L || lastRefill > now) {
            lastRefill = now
        }
        if (lives >= MAX_LIVES) {
            storage.livesCount = MAX_LIVES
            storage.livesLastRefillTimestamp = now
            return
        }
        val elapsed = now - lastRefill
        val gained = (elapsed / LIFE_REFILL_INTERVAL_MS).toInt()
        if (gained > 0) {
            lives = (lives + gained).coerceAtMost(MAX_LIVES)
            lastRefill = if (lives >= MAX_LIVES) now else lastRefill + gained * LIFE_REFILL_INTERVAL_MS
        }
        storage.livesCount = lives
        storage.livesLastRefillTimestamp = lastRefill
    }
}

expect fun currentTimeMillis(): Long
